import { useState, useCallback } from 'react';
import { useImportInvoiceMutation } from '../Api/invoiceImportApi';
import { validateFile, type FileValidationResult } from '../Utils/fileValidation';
import type { InvoiceImportResultDto } from '../Types/invoiceImport.types';
import type { UserInvoiceCorrections } from '../Types/userInvoiceCorrections';

/**
 * Custom hook for file upload logic
 * Handles validation, FormData creation, and mutation trigger
 */
export function useFileUpload() {
  const [importInvoice, { isLoading, error, data, reset }] = useImportInvoiceMutation();
  const [validationError, setValidationError] = useState<string | null>(null);

  /**
   * Validates and uploads file
   *
   * @param file - File to upload
   * @param sourceCountryIso - Optional ISO country code (e.g., "PL", "DE")
   * @param userCorrections - Optional user corrections from OCR preview (overrides OCR with 100% confidence)
   * @returns Import result DTO or null if validation failed
   */
  const uploadFile = useCallback(
    async (
      file: File,
      sourceCountryIso?: string,
      userCorrections?: UserInvoiceCorrections
    ): Promise<InvoiceImportResultDto | null> => {
      void userCorrections;

      // Reset previous state
      setValidationError(null);
      reset();

      // Client-side validation
      const validation: FileValidationResult = validateFile(file);
      if (!validation.isValid) {
        setValidationError(validation.errorMessage || 'Walidacja pliku nie powiodła się.');
        return null;
      }

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      const isKsefXmlFile = file.name.toLowerCase().endsWith('.xml');
      const effectiveSourceCountryIso = sourceCountryIso
        ? sourceCountryIso.toUpperCase()
        : isKsefXmlFile
          ? 'PL'
          : undefined;

      try {
        // Trigger mutation
        const result = await importInvoice({
          formData,
          sourceCountryIso: effectiveSourceCountryIso,
        }).unwrap();
        return result;
      } catch (err) {
        // Error handled by RTK Query (available in `error` state)
        console.error('File upload failed:', err);
        return null;
      }
    },
    [importInvoice, reset]
  );

  /**
   * Clears all state (errors, data, validation)
   */
  const clearState = useCallback(() => {
    setValidationError(null);
    reset();
  }, [reset]);

  return {
    uploadFile,
    clearState,
    isLoading,
    validationError,
    apiError: error,
    result: data,
  };
}
